import { useAuth } from '@/components/auth/authProvider';
import usePermissions from '@/hooks/usePermissions';
import {
  OrganizationMaintenanceSettingsResponse,
  useGetOrganizationMaintenanceSettingsQuery,
  usePutOrganizationMaintenanceSettingsMutation,
} from '@/services/organizations';
import { OrganizationNode } from '@/types/Organizations';
import {
  Box,
  Button,
  Card,
  SelectInputNative,
  Spinner,
  TextInput,
  toast,
  Toggle,
} from '@palmetto/palmetto-components';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Control, Controller, useController, useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router';

import { MaintenancePeriodUnit, MaintenanceRateUnit, ProgramType, ProgramTypeLabels, UserPermissions } from 'types';

import { isPalmettoFinanceUser } from '../../../auth/RequirePalmettoFinanceUser';

const PERIOD_UNIT_OPTIONS = [
  { label: 'Year(s)', value: MaintenancePeriodUnit.year },
  { label: 'Month(s)', value: MaintenancePeriodUnit.month },
  { label: 'Day(s)', value: MaintenancePeriodUnit.day },
];

// maintenanceRate is locked to perSystem; serviceRate is locked to hourly.
// These may need to expand to all MaintenanceRateUnit values in the future.
const MAINTENANCE_RATE_UNIT_OPTIONS = [{ label: 'Per System', value: MaintenanceRateUnit.perSystem }];
const SERVICE_RATE_UNIT_OPTIONS = [{ label: 'Hourly', value: MaintenanceRateUnit.hourly }];

interface MaintenanceSettingsFormValues {
  inherit: boolean;
  cadenceTimes: string;
  cadencePerAmount: string;
  cadencePerUnit: string;
  maintenanceWindowAmount: string;
  maintenanceWindowUnit: string;
  maintenanceRateAmount: string;
  maintenanceRateUnit: string;
  serviceRateAmount: string;
  serviceRateUnit: string;
}

const toNumber = (v: string): number | undefined => {
  const trimmed = v.trim();
  return trimmed === '' ? undefined : Number(trimmed);
};

const buildInitialValues = (
  data: OrganizationMaintenanceSettingsResponse | undefined,
): MaintenanceSettingsFormValues => {
  const value = data?.value;
  return {
    inherit: data?.inherit ?? true,
    cadenceTimes: value?.cadence?.times != null ? String(value.cadence.times) : '',
    cadencePerAmount: value?.cadence?.per?.amount != null ? String(value.cadence.per.amount) : '',
    cadencePerUnit: value?.cadence?.per?.unit ?? MaintenancePeriodUnit.year,
    maintenanceWindowAmount: value?.maintenanceWindow?.amount != null ? String(value.maintenanceWindow.amount) : '',
    maintenanceWindowUnit: value?.maintenanceWindow?.unit ?? MaintenancePeriodUnit.day,
    maintenanceRateAmount: value?.maintenanceRate?.amount != null ? String(value.maintenanceRate.amount) : '',
    maintenanceRateUnit: value?.maintenanceRate?.unit ?? MaintenanceRateUnit.perSystem,
    serviceRateAmount: value?.serviceRate?.amount != null ? String(value.serviceRate.amount) : '',
    serviceRateUnit: value?.serviceRate?.unit ?? MaintenanceRateUnit.hourly,
  };
};

const validatePositiveAmount = (v: string, { inherit }: MaintenanceSettingsFormValues): true | string => {
  if (inherit) return true;
  const trimmed = v.trim();
  if (!trimmed) return 'Required';
  const n = Number(trimmed);
  if (isNaN(n) || n <= 0) return 'Must be greater than 0';
  return true;
};

const validateRequiredWhenCustom = (v: string, { inherit }: MaintenanceSettingsFormValues): true | string => {
  if (inherit) return true;
  return v ? true : 'Required';
};

const InheritToggle = ({
  control,
  disabled,
  sourceName,
}: {
  control: Control<MaintenanceSettingsFormValues>;
  disabled: boolean;
  sourceName?: string;
}) => {
  const { field } = useController({ name: 'inherit', control });
  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      field.onChange(!event.target.checked);
    },
    [field],
  );
  return (
    <Box
      direction="row"
      borderColor="separator"
      borderWidth="sm"
      radius="md"
      background={field.value ? undefined : 'warning-50'}
      justifyContent="space-between"
      margin="lg 0"
    >
      <Box direction={{ base: 'column', tablet: 'row' }} margin="0 sm sm sm">
        <Box direction="column" childGap="xs">
          {field.value ? (
            <>
              <Box fontWeight="bold" margin="sm 0">
                Using Inherited Settings
              </Box>
              <Box>
                Settings are inherited from {sourceName ?? 'parent'}. Turn this on to configure custom settings.
              </Box>
            </>
          ) : (
            <>
              <Box fontWeight="bold" margin="sm 0">
                Using Custom Settings
              </Box>
              <Box>
                {sourceName
                  ? `Custom settings override anything inherited from ${sourceName}.`
                  : 'Custom settings are activated.'}
              </Box>
            </>
          )}
        </Box>
      </Box>
      <Box justifyContent="center" margin="sm">
        <Toggle
          id="inheritToggle"
          label="Use custom settings"
          hideLabel
          isChecked={!field.value}
          isDisabled={disabled}
          onChange={handleChange}
        />
      </Box>
    </Box>
  );
};

export type MaintenanceSettingsProps = { organization: OrganizationNode };

export const MaintenanceSettings = ({ organization }: MaintenanceSettingsProps) => {
  const alias = organization.alias;
  const permissions = usePermissions();
  const { claims } = useAuth();
  const navigate = useNavigate();
  const canManage = isPalmettoFinanceUser(claims) && permissions.includes(UserPermissions.admin);

  const [programType, setProgramType] = useState<ProgramType>(() => {
    const types = organization.programTypes ?? [];
    return types.includes(ProgramType.hvac) ? ProgramType.hvac : (types[0] ?? ProgramType.hvac);
  });

  const programTypeOptions = useMemo(() => {
    const EQUIPMENT_FIRST = [ProgramType.hvac, ProgramType.geothermal, ProgramType.generator];
    return [...(organization.programTypes ?? [])]
      .sort((a, b) => {
        const ai = EQUIPMENT_FIRST.indexOf(a);
        const bi = EQUIPMENT_FIRST.indexOf(b);
        return (ai === -1 ? Infinity : ai) - (bi === -1 ? Infinity : bi);
      })
      .map((pt) => ({ label: ProgramTypeLabels[pt] ?? pt, value: pt }));
  }, [organization.programTypes]);

  const { data, isLoading } = useGetOrganizationMaintenanceSettingsQuery({ alias, programType });
  const [putSettings] = usePutOrganizationMaintenanceSettingsMutation();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<MaintenanceSettingsFormValues>({
    defaultValues: buildInitialValues(undefined),
  });

  useEffect(() => {
    reset(buildInitialValues(data));
  }, [data, reset]);

  const inherit = useWatch({ control, name: 'inherit' });

  const onSubmit = useCallback(
    async (formData: MaintenanceSettingsFormValues) => {
      try {
        const body = formData.inherit
          ? { alias, programType, inherit: true as const }
          : {
              alias,
              programType,
              inherit: false as const,
              value: {
                cadence: {
                  times: toNumber(formData.cadenceTimes)!,
                  per: {
                    unit: formData.cadencePerUnit as MaintenancePeriodUnit,
                    amount: toNumber(formData.cadencePerAmount)!,
                  },
                },
                maintenanceWindow: {
                  unit: formData.maintenanceWindowUnit as MaintenancePeriodUnit,
                  amount: toNumber(formData.maintenanceWindowAmount)!,
                },
                maintenanceRate: {
                  unit: formData.maintenanceRateUnit as MaintenanceRateUnit,
                  amount: toNumber(formData.maintenanceRateAmount)!,
                },
                serviceRate: {
                  unit: formData.serviceRateUnit as MaintenanceRateUnit,
                  amount: toNumber(formData.serviceRateAmount)!,
                },
              },
            };
        await putSettings(body).unwrap();
        toast.success('Maintenance settings saved');
      } catch {
        toast.error('Failed to save maintenance settings');
      }
    },
    [alias, putSettings, programType],
  );

  const handleCancel = useCallback(() => navigate(`/settings/organizations/${alias}`), [navigate, alias]);

  if (isLoading) {
    return (
      <Box padding="lg" alignItems="center">
        <Spinner size="lg" />
      </Box>
    );
  }

  return (
    <Box padding="lg" childGap="lg">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Card>
          <Card.Section
            title={
              <Box direction="column" childGap="xs">
                <Box fontWeight="bold">Maintenance Settings</Box>
                <Box>Configure maintenance visit cadence, scheduling window, and labor rates.</Box>
              </Box>
            }
          >
            <Box childGap="lg" margin="md 0">
              {(organization.programTypes ?? []).length > 1 && (
                <SelectInputNative
                  label="Program Type"
                  name="programType"
                  id="programType"
                  options={programTypeOptions}
                  onChange={(e) => setProgramType(e.target.value as ProgramType)}
                  value={programType}
                  isDisabled={!canManage}
                  maxWidth="2xl"
                />
              )}
              <InheritToggle control={control} disabled={!canManage} />
              {!inherit && (
                <Box childGap="xs">
                  <Box>
                    <Box fontWeight="bold" margin="sm 0">
                      Cadence
                    </Box>
                    <Box direction="row" gap="md" alignItems="flex-start" style={{ minHeight: '5.5rem' }}>
                      <Controller
                        name="cadenceTimes"
                        control={control}
                        rules={{ validate: validatePositiveAmount }}
                        render={({ field, fieldState }) => (
                          <TextInput
                            label="Times"
                            name={field.name}
                            id="cadenceTimes"
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            error={fieldState.error?.message}
                            isDisabled={!canManage}
                            width="3xl"
                          />
                        )}
                      />
                      <Box margin="xl 0 0 0" color="grey-500">
                        per
                      </Box>
                      <Controller
                        name="cadencePerAmount"
                        control={control}
                        rules={{ validate: validatePositiveAmount }}
                        render={({ field, fieldState }) => (
                          <TextInput
                            label="Amount"
                            name={field.name}
                            id="cadencePerAmount"
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            error={fieldState.error?.message}
                            isDisabled={!canManage}
                            width="3xl"
                          />
                        )}
                      />
                      <Controller
                        name="cadencePerUnit"
                        control={control}
                        rules={{ validate: validateRequiredWhenCustom }}
                        render={({ field }) => (
                          <SelectInputNative
                            label="Unit"
                            name={field.name}
                            id="cadencePerUnit"
                            options={PERIOD_UNIT_OPTIONS}
                            onChange={field.onChange}
                            value={field.value}
                            isDisabled={!canManage}
                            width="3xl"
                          />
                        )}
                      />
                    </Box>
                  </Box>
                  <Box>
                    <Box fontWeight="bold" margin="sm 0">
                      Maintenance Window
                    </Box>
                    <Box direction="row" gap="md" alignItems="flex-start" style={{ minHeight: '5.5rem' }}>
                      <Controller
                        name="maintenanceWindowAmount"
                        control={control}
                        rules={{ validate: validatePositiveAmount }}
                        render={({ field, fieldState }) => (
                          <TextInput
                            label="Amount"
                            name={field.name}
                            id="maintenanceWindowAmount"
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            error={fieldState.error?.message}
                            isDisabled={!canManage}
                            width="3xl"
                          />
                        )}
                      />
                      <Controller
                        name="maintenanceWindowUnit"
                        control={control}
                        rules={{ validate: validateRequiredWhenCustom }}
                        render={({ field }) => (
                          <SelectInputNative
                            label="Unit"
                            name={field.name}
                            id="maintenanceWindowUnit"
                            options={PERIOD_UNIT_OPTIONS}
                            onChange={field.onChange}
                            value={field.value}
                            isDisabled={!canManage}
                            width="3xl"
                          />
                        )}
                      />
                    </Box>
                  </Box>
                  <Box>
                    <Box fontWeight="bold" margin="sm 0">
                      Maintenance Rate
                    </Box>
                    <Box direction="row" gap="md" alignItems="flex-start" style={{ minHeight: '5.5rem' }}>
                      <Controller
                        name="maintenanceRateAmount"
                        control={control}
                        rules={{ validate: validatePositiveAmount }}
                        render={({ field, fieldState }) => (
                          <TextInput
                            label="Amount"
                            name={field.name}
                            id="maintenanceRateAmount"
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            error={fieldState.error?.message}
                            prefix="$"
                            isDisabled={!canManage}
                            width="3xl"
                          />
                        )}
                      />
                      <Controller
                        name="maintenanceRateUnit"
                        control={control}
                        render={({ field }) => (
                          <SelectInputNative
                            label="Unit"
                            name={field.name}
                            id="maintenanceRateUnit"
                            options={MAINTENANCE_RATE_UNIT_OPTIONS}
                            onChange={field.onChange}
                            value={field.value}
                            isDisabled
                            width="3xl"
                          />
                        )}
                      />
                    </Box>
                  </Box>
                  <Box>
                    <Box fontWeight="bold" margin="sm 0">
                      Service Rate
                    </Box>
                    <Box direction="row" gap="md" alignItems="flex-start" style={{ minHeight: '5.5rem' }}>
                      <Controller
                        name="serviceRateAmount"
                        control={control}
                        rules={{ validate: validatePositiveAmount }}
                        render={({ field, fieldState }) => (
                          <TextInput
                            label="Amount"
                            name={field.name}
                            id="serviceRateAmount"
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            error={fieldState.error?.message}
                            prefix="$"
                            isDisabled={!canManage}
                            width="3xl"
                          />
                        )}
                      />
                      <Controller
                        name="serviceRateUnit"
                        control={control}
                        render={({ field }) => (
                          <SelectInputNative
                            label="Unit"
                            name={field.name}
                            id="serviceRateUnit"
                            options={SERVICE_RATE_UNIT_OPTIONS}
                            onChange={field.onChange}
                            value={field.value}
                            isDisabled
                            width="3xl"
                          />
                        )}
                      />
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </Card.Section>
          {canManage && (
            <Card.Section>
              <Box
                direction={{ base: 'column', tablet: 'row' }}
                alignItems={{ base: 'stretch', tablet: 'flex-end' }}
                justifyContent={{ tablet: 'flex-end' }}
                childGap="sm"
                style={{ flexShrink: 0 }}
              >
                <Button
                  as="a"
                  navigate={handleCancel}
                  variant="secondary"
                  tone="neutral"
                  size="md"
                  isDisabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button variant="primary" size="md" type="submit" isDisabled={isSubmitting} isLoading={isSubmitting}>
                  Save
                </Button>
              </Box>
            </Card.Section>
          )}
        </Card>
      </form>
    </Box>
  );
};
